import { init, isInitialized } from '@fullstory/browser'
import { useEffect } from 'react'
import { logger } from 'utils/logger'

interface UseFullStoryOptions {
  anonymous?: boolean
  orgId?: string | undefined
  sampleRate?: number
  scoped?: boolean
}

/**
 * Hook to initialize and configure FullStory analytics
 *
 * Related environment variables:
 * - FULLSTORY_ORG_ID: Our FullStory org id (not client orgId), deliberately the same across all
 * environments
 * - FULLSTORY_SAMPLE_RATE: Percentage of sessions to record (0-100), if 0, FullStory is disabled
 * @param {Object} options - Configuration options for FullStory
 * @param {boolean} [options.anonymous=false] - When true, enables anonymous tracking mode
 * @param {string} [options.orgId] - FullStory organization ID
 * @param {number} [options.sampleRate=-1] - Percentage of sessions to record (0-100). -1 disables
 * @param {boolean} [options.scoped=false] - When true, enables cross-domain iFrame recording
 * sampling
 * @returns {void}
 */
export default function useFullStory({
  anonymous = false,
  orgId,
  sampleRate = 0,
  scoped = false,
}: UseFullStoryOptions) {
  useEffect(() => {
    if (!orgId || isInitialized() || !shouldFullstoryStart(sampleRate)) return

    init(
      {
        orgId,
        recordCrossDomainIFrames: scoped,
        recordOnlyThisIFrame: scoped,
      },
      ({ sessionUrl, settings }: { sessionUrl: string; settings: Readonly<object> }) => {
        logger('debug', 'FullStory: FullStory initialized', { context: { sessionUrl, settings } })
      },
    )
  }, [anonymous, orgId, sampleRate, scoped])
}

/**
 * Determine whether FullStory should be started for the current browser session.
 *
 * Behavior:
 * - Reads a persisted sampling decision from sessionStorage using key "_fs_gfm_is_enabled".
* - If no prior decision exists, samples the session using the provided `sampleRate` (0–100),
  persists the boolean decision to sessionStorage, and returns it.
 *
 * Returns:
 * - true if this session should be recorded by FullStory; false otherwise.
 *
 * Side effects:
 * - Reads from and writes to window.sessionStorage.
 * - May log errors via logger if sessionStorage access fails.
 *
 * Notes:
 * - Intended for browser environments only (requires window).
 */

const shouldFullstoryStart = (sampleRate: number) => {
  const FS_IS_ENABLED_STORAGE_KEY = '_fs_gfm_is_enabled'

  let enableFullstory: boolean | null = null

  // * Attempt to retrieve a Fullstory start session decision from sessionStorage
  try {
    const sessionKeyVal = window.sessionStorage.getItem(FS_IS_ENABLED_STORAGE_KEY)

    if (sessionKeyVal !== null) {
      enableFullstory = sessionKeyVal === 'true'
    }
  } catch (e) {
    logger('error', new Error('Failed to parse sessionStorage', { cause: e }), {
      context: { key: FS_IS_ENABLED_STORAGE_KEY },
    })
  }

  // * If there is no key in session storage attempt to randomly start user session
  if (enableFullstory === null) {
    enableFullstory = Math.random() * 100 < sampleRate

    window.sessionStorage.setItem(FS_IS_ENABLED_STORAGE_KEY, String(enableFullstory))
  }

  return enableFullstory
}
